import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { auth, db } from "../../lib/firebase";
import {
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
} from "firebase/auth";
import { setDoc, doc } from "firebase/firestore";
import MainButton from "../../components/Button";
import { Lock, Mail, User, Eye, EyeOff } from "lucide-react";
import { Select } from "antd";
import { useNavigate } from "react-router-dom";
import { storageConst } from "../../utils/constants";
import SpinnerIcon from "../../assets/svgs/spinner";
import Notification from "../../components/notification";

// Updated schema: add confirmPassword, zipCode; remove address
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  accountType: yup.string().required("Account type is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
  zipCode: yup
    .string()
    .matches(/^\d{5}$/, "Zip code must be 5 digits")
    .required("Zip code is required"),
});

const SignUpPage = () => {
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleNavigation = () => {
    navigate("/signin");
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: {
    name: string;
    email: string;
    accountType: string;
    password: string;
    confirmPassword: string;
    zipCode: string;
  }) => {
    try {
      setIsLoader(true);
      // Convert zip code to lat/long using Google Maps Geocoding API
      let lat = null;
      let long = null;
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
        const geoRes = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            data.zipCode
          )}&key=${apiKey}`
        );
        const geoData = await geoRes.json();
        if (
          geoData.status === "OK" &&
          geoData.results &&
          geoData.results.length > 0 &&
          geoData.results[0].geometry
        ) {
          lat = geoData.results[0].geometry.location.lat;
          long = geoData.results[0].geometry.location.lng;
        }
      } catch (geoErr) {
        // If geocoding fails, lat/long remain null
      }
      // Firebase signup
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      // Optionally update display name
      if (userCredential.user) {
        await updateProfile(userCredential.user, { displayName: data.name });
        // Send verification email
        await sendEmailVerification(userCredential.user);
        // Store user profile in Firestore
        await setDoc(doc(db, "users", userCredential.user.uid), {
          name: data.name,
          uid: userCredential.user.uid,
          isbusiness: data.accountType === "business",
          email: data.email.trim(),
          password: data.password, // (Consider not storing plain password in production)
          zipcode: data.zipCode,
          about: "",
          avatar: "",
          lat,
          long,
          followers: [],
          following: [],
          active: data.accountType === "user" ? true : false,
          role: "USER",
        });

        setNotification({
          type: "Success",
          message:
            "Signup successfully. Verification email sent. Please check your inbox",
        });
        setIsLoader(false);
      }
    } catch (error: any) {
      setNotification({
        type: "Error",
        message:
          error?.message || "Failed to sign up. Please check your details.",
      });
      setIsLoader(false);
    }
  };

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setShow(false);
        setNotification({ type: "", message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div
      className=" bg-bg bg-center min-h-screen w-full py-4 flex items-center justify-center"
      style={{
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="bg-bgCard px-8 py-10 rounded-2xl shadow-xl w-full max-w-lg flex flex-col gap-y-7">
        <div className="mb-2">
          <h2 className="font-bold text-3xl text-center text-primary mb-1 tracking-tight">
            Sign Up
          </h2>
          <p className="text-center text-textSecondary text-base">
            Create your account to get started
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div className="relative">
            <User className="absolute left-3 top-3.5 h-5 w-5 text-text" />
            <input
              type="text"
              {...register("name")}
              className={`w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                errors.name ? "border-primary" : "border-text"
              }`}
              placeholder="Full Name"
              autoComplete="name"
            />
            {errors.name && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.name.message}
              </p>
            )}
          </div>
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-3 top-3.5 h-5 w-5 text-text" />
            <input
              type="email"
              {...register("email")}
              className={`w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                errors.email ? "border-primary" : "border-text"
              }`}
              placeholder="Email Address"
              autoComplete="email"
            />
            {errors.email && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>
          {/* Account Type */}
          <div className="relative">
            <Select
              className={`w-full
                [&_.ant-select-selector]:!h-[50px]
                [&_.ant-select-selector]:!rounded-full
                [&_.ant-select-selector]:!border
                [&_.ant-select-selector]:!border-text
                [&_.ant-select-selector]:!bg-bgCard
                [&_.ant-select-selector]:!text-text
                [&_.ant-select-selector]:!pl-4
                [&_.ant-select-selector]:!pr-10
                [&_.ant-select-selector]:!flex
                [&_.ant-select-selector]:!items-center
                [&_.ant-select-selection-placeholder]:!text-text
                [&_.ant-select-dropdown]:!bg-bg
                [&_.ant-select-dropdown]:!p-2
                ${
                  errors.accountType
                    ? "[&_.ant-select-selector]:!border-primary"
                    : ""
                }
              `}
              size="large"
              placeholder="Select Account Type"
              options={[
                { value: "user", label: "User Account" },
                { value: "business", label: "Business Account" },
              ]}
              onChange={(value) =>
                setValue("accountType", value, { shouldValidate: true })
              }
              value={watch("accountType")}
              status={errors.accountType ? "error" : ""}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
            {errors.accountType && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.accountType.message}
              </p>
            )}
          </div>
          {/* Zip Code */}
          <div className="relative">
            <input
              type="text"
              {...register("zipCode")}
              className={`w-full pl-4 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                errors.zipCode ? "border-primary" : "border-text"
              }`}
              placeholder="Zip Code"
              autoComplete="postal-code"
              maxLength={5}
            />
            {errors.zipCode && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.zipCode.message}
              </p>
            )}
          </div>
          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 h-5 w-5 text-text" />
            <input
              type={showPassword ? "text" : "password"}
              {...register("password")}
              className={`w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                errors.password ? "border-error" : "border-text"
              }`}
              placeholder="Password"
              autoComplete="new-password"
            />
            <span
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute cursor-pointer top-4 right-2 flex justify-center items-center text-gray-500"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-text" />
              ) : (
                <Eye className="h-5 w-5 text-text" />
              )}
            </span>
            {errors.password && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.password.message}
              </p>
            )}
          </div>
          {/* Confirm Password */}
          <div className="relative">
            <Lock className="absolute left-3 top-3.5 h-5 w-5 text-text" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirmPassword")}
              className={`w-full px-9 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full ${
                errors.confirmPassword ? "border-error" : "border-text"
              }`}
              placeholder="Confirm Password"
              autoComplete="new-password"
            />
            <span
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute cursor-pointer top-4 right-2 flex justify-center items-center text-gray-500"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-text" />
              ) : (
                <Eye className="h-5 w-5 text-text" />
              )}
            </span>
            {errors.confirmPassword && (
              <p className="text-gradientTwo text-sm mt-1">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>
          <MainButton
            type="submit"
            label={isLoader ? <SpinnerIcon /> : "Sign Up"}
            className=" mt-5 bg-primary h-[50px] focus:outline-none focus:ring-0 rounded-full justify-center items-center gap-2.5 inline-flex w-full text-base font-medium "
          />
        </form>
        {notification.message && (
          <Notification
            type={notification.type}
            label={notification.message}
            onClick={() => setShow(true)}
          />
        )}
        <div className="flex gap-x-2 justify-center mt-4">
          <span className="text-text text-base">Already have an account?</span>
          <button
            className="text-primary font-semibold hover:underline"
            onClick={handleNavigation}
          >
            Log in
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
